import type { BaseNodeData } from '@/shared/lib/node-registry'
import {
  resolveNodeOpData,
  resolveOpPreferenceKeys,
  type OpDataResolveOptions,
} from '@/shared/lib/op-registry'
import {
  clearOpPreference,
  clearOpPreferenceFields,
  patchOpPreference,
  replaceOpPreference,
} from '@/shared/lib/op-preference-storage'

export interface NodeOpDataSource {
  op_data?: Record<string, unknown>
}

/**
 * OP 参数存储语义：
 * - op_data[op] 里存在的字段表示“已经被当前节点固定”的字段。
 * - op_data[op] 里缺失的字段表示“跟随当前 OP 默认值”。
 * - 因此写入时不再和默认值比较；即使写入值刚好等于默认值，也要保留。
 * - 只有显式 clear 字段或 clear 整个 OP 时，才恢复为跟随默认值。
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasKeys(value: Record<string, unknown>) {
  return Object.keys(value).length > 0
}

function getPreferenceKeys(op: string) {
  return resolveOpPreferenceKeys(op)
}

/**
 * 读取 OP 参数。
 *
 * 返回值会把 op registry 默认值和已存 op_data[op] 合并：
 * - 业务 UI / payload 构造可以直接读取完整参数。
 * - 缺失字段自动走默认值，不需要为了默认值写入同步数据。
 */
export function getNodeOpData<T extends object>(
  nodeData: BaseNodeData,
  options?: OpDataResolveOptions,
): T {
  return resolveNodeOpData<T>(nodeData, options)
}

/**
 * 替换当前 OP 的已固定字段集合。
 *
 * 注意这里的 next 不是“完整默认快照”，而是“需要固定到节点上的字段”。
 * 如果传入空对象，表示当前 OP 没有任何固定字段，会删除 op_data[op]。
 */
export function replaceNodeOpData<T extends object>(
  nodeData: NodeOpDataSource,
  op: string,
  next: T,
) {
  const currentOpData = isRecord(nodeData.op_data) ? nodeData.op_data : {}
  const nextOpData = { ...currentOpData }

  if (hasKeys(next as Record<string, unknown>)) {
    nextOpData[op] = next
  } else {
    delete nextOpData[op]
  }

  if (hasKeys(nextOpData)) {
    nodeData.op_data = nextOpData
  } else {
    delete nodeData.op_data
  }

  if (hasKeys(next as Record<string, unknown>)) {
    replaceOpPreference(op, next as Record<string, unknown>, getPreferenceKeys(op))
  } else {
    clearOpPreference(op)
  }
}

/**
 * 追加或更新当前 OP 的固定字段。
 *
 * 只会基于已存 op_data[op] 合并 patch，不会先合并 defaults。
 * 这样用户只改一个字段时，不会把整份默认参数写进同步数据。
 */
export function patchNodeOpData<T extends object>(
  nodeData: NodeOpDataSource,
  op: string,
  patch: Partial<T>,
) {
  if (!isRecord(nodeData.op_data)) {
    nodeData.op_data = {}
  }
  if (!isRecord(nodeData.op_data[op])) {
    nodeData.op_data[op] = {}
  }
  
  const target = nodeData.op_data[op] as Record<string, unknown>
  for (const [key, value] of Object.entries(patch)) {
    target[key] = value
  }

  patchOpPreference(op, patch as Record<string, unknown>, getPreferenceKeys(op))
}

/**
 * 清空某个 OP 的所有固定字段。
 *
 * 常用于“重置为默认/跟随默认值”场景。
 */
export function clearNodeOpData(nodeData: NodeOpDataSource, op: string) {
  const currentOpData = isRecord(nodeData.op_data) ? nodeData.op_data : {}
  const nextOpData = { ...currentOpData }
  delete nextOpData[op]

  if (hasKeys(nextOpData)) {
    nodeData.op_data = nextOpData
  } else {
    delete nodeData.op_data
  }

  clearOpPreference(op)
}

/**
 * 清空某个 OP 的部分固定字段。
 *
 * 被清掉的字段会重新跟随 defaults；其他已固定字段保持不变。
 */
export function clearNodeOpDataFields(
  nodeData: NodeOpDataSource,
  op: string,
  fields: Iterable<string>,
) {
  const fieldList = Array.from(fields)
  if (!isRecord(nodeData.op_data) || !isRecord(nodeData.op_data[op])) {
    clearOpPreferenceFields(op, fieldList)
    return
  }

  const target = nodeData.op_data[op] as Record<string, unknown>
  for (const field of fieldList) {
    delete target[field]
  }

  if (!hasKeys(target)) {
    delete nodeData.op_data[op]
  }
  if (!hasKeys(nodeData.op_data)) {
    delete nodeData.op_data
  }

  clearOpPreferenceFields(op, fieldList)
}
